// Shared system-status reads — the cross-repo version/health matrix from
// /core/system/status on the main server (Mongo; formerly a Supabase edge
// function) + 120-day uptime data from /core/system/uptime (Mongo; formerly
// the /workspace-project/sb system_uptime table — Supabase is retired, both
// routes are Mongo-only now). Read by the System Health page (workspace
// pages/system.js, through el.call) and the tickets release page.
//
// Both reads go through the SDK (sdk 3.14.794: getSystemStatus /
// getSystemUptime), never a raw fetch — the backend is SDK-only. The SDK owns
// the URL (the same workspaceApiBase channel getSDK() resolves), the bearer
// (the TokenManager session, a per-tab persona included — the old
// localStorage coreHeaders() read skipped the persona) and the transport
// retry. DESIGN-SHARED-SYSTEMSTATUS-RAW-FETCH-ONTO-SDK-1.
//
// Why /core/system/status replaced the edge function: the edge fn read
// `GITHUB_TOKEN` from Deno env. That PAT was scope-limited / expired and
// rotating it was operator pain. The main server has `core/lib/githubApp.js`
// which auths as the symbols-agent GitHub App — installation tokens
// auto-discovered via /orgs/{org}/installation, auto-rotated every hour.
// Installing the App on a new org via GitHub's UI is now the entire flow;
// no secret rotation, no PAT scoping.

import { getSDK } from './sdk.js'

export async function fetchSystemStatus() {
  // FA204 / frank trap — inlined (self-contained local, see longPress.js for
  // the identical shape). 10s ceiling so the page never sits indefinitely
  // on "Fetching status…" when the request hangs or the network is
  // unreachable. The signal rides into the SDK request.
  const TIMEOUT_MS = 10000
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    // Public read — the route has no auth gate. The SDK still attaches the
    // session bearer, which a public route ignores.
    const data = await getSDK().getSystemStatus({ signal: controller.signal })
    // The null client (a host where the SDK class never arrived) answers
    // null: no backend was reached, so fail like any other failure. Never
    // resolve null — both callers read `.matrix` off the answer.
    if (!data || typeof data !== 'object') throw new Error('system-status: no response')
    return data
  } catch (err) {
    // An HTTP refusal keeps the old status-only message. The SDK's own error
    // text is the SERVER's payload (a GitHub 401 body, for example), and the
    // tickets release page renders the message.
    if (err?.status !== undefined) throw new Error(`system-status: ${err.status}`)
    throw err
  } finally {
    clearTimeout(timer)
  }
}

export async function fetchUptime() {
  // /core/system/uptime on the main server (superadmin-only; answers the
  // grouped { [service]: [ { service, checked_at, status, latency_ms,
  // message }, … ] } shape — returned as-is).
  // FA204 / frank trap — inlined, see fetchSystemStatus above.
  const TIMEOUT_MS = 10000
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    return (await getSDK().getSystemUptime({ limit: 3000, signal: controller.signal })) || {}
  } catch (err) {
    // An HTTP refusal (a 403 for a non-superadmin) answers {} silently, as
    // the raw read did on !res.ok. Only a request that never completed warns.
    if (err?.status === undefined) {
      console.warn('[systemStatus] /core/system/uptime fetch failed', err)
    }
    return {}
  } finally {
    clearTimeout(timer)
  }
}
